import type { EffectivePriorityCtx, Task } from "@app/core";
import { effectivePriorityOf, effectiveUrgencyOf, PRIORITY_RANK } from "@app/core";
import { Badge, Button, Card, ChevronsUpDown, Flag, Plus, Repeat } from "@app/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTaskDialogStore, useTasksStore } from "../../stores/index.js";
import { useEffectivePriorityCtx } from "../../features/priority-matrix/useEffectiveMatrixDisplay.js";
import { PRIORITY_FLAG_HEX } from "../../features/task-colors.js";
import { formatDuration } from "../../lib/format.js";
import type { I18nKey } from "../../lib/language.js";
import { useMobileUx } from "../../lib/useMobileUx.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { useI18n } from "../../lib/useI18n.js";
import { useViewFilters } from "../../lib/useViewFilters.js";
import { ShowMoreFooter, usePagedRows } from "../../lib/paged-list.js";
import { occurrenceRefOf } from "../../lib/virtual-id.js";
import { AnimatePresence, motion, useReducedMotion } from "../../lib/motion/index.js";
import { type NextInstanceCache, tasksForList } from "./next-instances.js";

type SortKey = "due" | "deadline" | "duration" | "priority" | "urgency" | "title";
type SortDir = "asc" | "desc";

const GRID_COLS =
  "auto minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.5fr) auto";

const SORT_KEYS: SortKey[] = ["due", "priority", "urgency", "deadline", "duration", "title"];
const SORT_LABEL_KEY: Record<SortKey, I18nKey> = {
  due: "tasks.due",
  deadline: "tasks.deadline",
  duration: "tasks.duration",
  priority: "tasks.priority",
  urgency: "tasks.urgency",
  title: "tasks.titleColumn",
};

export function TasksListRoute() {
  const { t: tr } = useI18n();
  const tasks = useTasksStore((s) => s.tasks);
  const rules = useTasksStore((s) => s.rules);
  const overrides = useTasksStore((s) => s.overrides);
  const loaded = useTasksStore((s) => s.loaded);
  const hydrate = useTasksStore((s) => s.hydrate);
  const complete = useTasksStore((s) => s.complete);
  const completeOccurrence = useTasksStore((s) => s.completeOccurrence);
  const applyFilters = useViewFilters();
  const tf = useTimeFormat();
  const reduce = useReducedMotion();
  const mobile = useMobileUx();
  const openCreate = useTaskDialogStore((s) => s.openCreate);
  const openEdit = useTaskDialogStore((s) => s.openEdit);
  useEffect(() => { if (!loaded) void hydrate(); }, [loaded, hydrate]);

  const [sortKey, setSortKey] = useState<SortKey>("due");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // Day-anchored context so the priority/urgency columns + sorts follow deadlines
  // exactly like the sidebar filter (recomputes at the day boundary).
  const urgencyCtx = useEffectivePriorityCtx();

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir(k === "priority" || k === "urgency" ? "desc" : "asc"); }
  }

  function toggleComplete(t: Task, done: boolean) {
    if (t.virtual && t.parent_id) void completeOccurrence(t.parent_id, occurrenceRefOf(t), done);
    else void complete(t.id, done);
  }

  const nextCache = useRef<NextInstanceCache>(new Map());
  const sorted = useMemo(() => {
    const arr = tasksForList(applyFilters(tasks), rules, overrides, new Date(), nextCache.current);
    arr.sort((a, b) => {
      const cmp = compare(a, b, sortKey, urgencyCtx);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [tasks, applyFilters, rules, overrides, sortKey, sortDir, urgencyCtx]);

  // The list is un-virtualized and every row is an `AnimatePresence` motion
  // element: on a flat dataset it is the app's biggest single render (8.9k rows
  // ≈ 122k DOM nodes). Page it — re-sorting or re-filtering pages back to the
  // top, which is where a re-sort is read from anyway.
  const { shown, total, hasMore, showMore } = usePagedRows(sorted, `${sortKey}:${sortDir}`);

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{tr("tasks.title")}</h1>
        <Button onClick={() => openCreate()}><Plus className="h-4 w-4" /> {tr("common.new")}</Button>
      </header>
      <Card className="p-0 overflow-hidden">
        {sorted.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">{tr("tasks.empty")}</p>
        ) : mobile ? (
          <div className="flex flex-col text-sm">
            <div className="no-scrollbar flex gap-1 overflow-x-auto border-b bg-muted/40 px-3 py-2">
              {SORT_KEYS.map((key) => (
                <SortPill
                  key={key}
                  label={tr(SORT_LABEL_KEY[key])}
                  active={sortKey === key}
                  dir={sortDir}
                  onClick={() => toggleSort(key)}
                />
              ))}
            </div>
            <ul className="flex flex-col divide-y divide-border">
              <AnimatePresence initial={false}>
              {shown.map((t) => {
                const done = t.status === "done";
                // Live priority — same rule as the desktop PriorityCell, so the
                // phone card never shows a different flag than the table.
                const level = effectivePriorityOf(t, urgencyCtx);
                return (
                  <motion.li
                    key={t.id}
                    layout={reduce ? false : "position"}
                    initial={reduce ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={reduce ? { opacity: 0 } : { opacity: 0, height: 0 }}
                    transition={{ duration: reduce ? 0 : 0.15 }}
                    onClick={() => openEdit(t)}
                    className="flex cursor-pointer gap-3 px-3 py-3 hover:bg-accent/30"
                    title={tr("tasks.tapToEdit")}
                  >
                    <input
                      type="checkbox"
                      checked={done}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => toggleComplete(t, e.target.checked)}
                      className="mt-0.5 h-5 w-5 shrink-0 rounded border-input"
                      aria-label={done ? tr("tasks.markOpen") : tr("tasks.markDone")}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-start gap-2">
                        <span className={`min-w-0 flex-1 truncate font-medium ${done ? "text-muted-foreground line-through" : ""}`}>
                          {t.title || <em className="text-muted-foreground">{tr("agenda.untitled")}</em>}
                        </span>
                        {t.recurrence_id && <Repeat className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-label={tr("agenda.recurring")} />}
                        {level !== "none" && (
                          <Badge tone={level} className="shrink-0 gap-1">
                            <Flag className="h-3 w-3" fill={PRIORITY_FLAG_HEX[level]} stroke={PRIORITY_FLAG_HEX[level]} />
                            {level}
                          </Badge>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        <span>{t.due_at ? tf.stamp(t.due_at) : tr("tasks.noDate")}</span>
                        {t.deadline_at && <span>{tr("tasks.deadlineAt", { time: tf.stamp(t.deadline_at) })}</span>}
                        <span>{formatDuration(t.duration_minutes)}</span>
                      </div>
                      {t.notes && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{t.notes}</p>}
                    </div>
                  </motion.li>
                );
              })}
              </AnimatePresence>
            </ul>
            {hasMore && <ShowMoreFooter shown={shown.length} total={total} onShowMore={showMore} />}
          </div>
        ) : (
          <div className="w-full text-sm">
            <div className="grid items-center gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground"
              style={{ gridTemplateColumns: GRID_COLS }}>
              <span />
              <HeaderCell label={tr("tasks.titleColumn")} active={sortKey === "title"} dir={sortDir} onClick={() => toggleSort("title")} />
              <HeaderCell label={tr("tasks.due")} active={sortKey === "due"} dir={sortDir} onClick={() => toggleSort("due")} />
              <HeaderCell label={tr("tasks.deadline")} active={sortKey === "deadline"} dir={sortDir} onClick={() => toggleSort("deadline")} />
              <HeaderCell label={tr("tasks.duration")} active={sortKey === "duration"} dir={sortDir} onClick={() => toggleSort("duration")} />
              <HeaderCell label={tr("tasks.priority")} active={sortKey === "priority"} dir={sortDir} onClick={() => toggleSort("priority")} />
              <HeaderCell label={tr("tasks.urgency")} active={sortKey === "urgency"} dir={sortDir} onClick={() => toggleSort("urgency")} />
              <span>{tr("common.notes")}</span>
              <span />
            </div>
            <ul className="flex flex-col divide-y divide-border">
              <AnimatePresence initial={false}>
              {shown.map((t) => {
                const done = t.status === "done";
                return (
                  <motion.li
                    key={t.id}
                    layout={reduce ? false : "position"}
                    initial={reduce ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={reduce ? { opacity: 0 } : { opacity: 0, height: 0 }}
                    transition={{ duration: reduce ? 0 : 0.15 }}
                    onDoubleClick={() => openEdit(t)}
                    className="grid cursor-pointer items-center gap-2 overflow-hidden px-3 py-2 hover:bg-accent/30"
                    style={{ gridTemplateColumns: GRID_COLS }}
                    title={tr("tasks.doubleClickToEdit")}
                  >
                    <input
                      type="checkbox"
                      checked={done}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => toggleComplete(t, e.target.checked)}
                      className="h-4 w-4 rounded border-input"
                    />
                    <div className="flex min-w-0 items-center gap-2">
                      <span className={`truncate ${done ? "text-muted-foreground line-through" : ""}`}>{t.title || <em className="text-muted-foreground">{tr("agenda.untitled")}</em>}</span>
                      {t.recurrence_id && <Repeat className="h-3 w-3 shrink-0 text-muted-foreground" aria-label={tr("agenda.recurring")} />}
                    </div>
                    <span className="truncate text-xs text-muted-foreground">{t.due_at ? tf.stamp(t.due_at) : "—"}</span>
                    <span className="truncate text-xs text-muted-foreground">{t.deadline_at ? tf.stamp(t.deadline_at) : "—"}</span>
                    <span className="truncate text-xs">{formatDuration(t.duration_minutes)}</span>
                    <PriorityCell task={t} ctx={urgencyCtx} />
                    <UrgencyCell task={t} ctx={urgencyCtx} />
                    <span className="truncate text-xs text-muted-foreground">{t.notes || "—"}</span>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(t); }}>{tr("common.edit")}</Button>
                  </motion.li>
                );
              })}
              </AnimatePresence>
            </ul>
            {hasMore && <ShowMoreFooter shown={shown.length} total={total} onShowMore={showMore} />}
          </div>
        )}
      </Card>
    </div>
  );
}

interface SortPillProps { label: string; active: boolean; dir: SortDir; onClick: () => void }
function SortPill({ label, active, dir, onClick }: SortPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-3 py-1.5 text-xs font-medium ${active ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground"}`}
    >
      {label}
      <ChevronsUpDown className={`h-3 w-3 ${active ? "opacity-100" : "opacity-40"} ${active && dir === "desc" ? "rotate-180" : ""}`} />
    </button>
  );
}

interface HeaderCellProps { label: string; active: boolean; dir: SortDir; onClick: () => void }
function HeaderCell({ label, active, dir, onClick }: HeaderCellProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1 text-left hover:text-foreground ${active ? "text-foreground" : ""}`}
    >
      {label}
      <ChevronsUpDown className={`h-3 w-3 ${active ? "opacity-100" : "opacity-30"} ${active && dir === "desc" ? "rotate-180" : ""}`} />
    </button>
  );
}

/** Urgency third of `matrix_pos`, same colored-square language as the sidebar
 *  filter; unplaced tasks show a dash. */
/** Priority column — the LIVE (matrix-derived) priority so it tracks urgency. */
function PriorityCell({ task, ctx }: { task: Task; ctx: EffectivePriorityCtx }) {
  const level = effectivePriorityOf(task, ctx);
  if (level === "none") return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span>
      <Badge tone={level} className="gap-1">
        <Flag className="h-3 w-3" fill={PRIORITY_FLAG_HEX[level]} stroke={PRIORITY_FLAG_HEX[level]} />
        {level}
      </Badge>
    </span>
  );
}

function UrgencyCell({ task, ctx }: { task: Task; ctx: EffectivePriorityCtx }) {
  const level = effectiveUrgencyOf(task, ctx);
  if (level === "none") return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className="flex items-center gap-1 text-xs">
      <span className="h-2 w-2 shrink-0 rounded-sm border" style={{ background: PRIORITY_FLAG_HEX[level] }} />
      {level}
    </span>
  );
}

function compare(a: Task, b: Task, key: SortKey, urgencyCtx: EffectivePriorityCtx): number {
  switch (key) {
    case "due": {
      // null sorts after non-null
      if (!a.due_at && !b.due_at) return 0;
      if (!a.due_at) return 1;
      if (!b.due_at) return -1;
      return a.due_at.localeCompare(b.due_at);
    }
    case "deadline": {
      if (!a.deadline_at && !b.deadline_at) return 0;
      if (!a.deadline_at) return 1;
      if (!b.deadline_at) return -1;
      return a.deadline_at.localeCompare(b.deadline_at);
    }
    case "duration":
      return a.duration_minutes - b.duration_minutes;
    case "priority":
      return PRIORITY_RANK[effectivePriorityOf(a, urgencyCtx)] - PRIORITY_RANK[effectivePriorityOf(b, urgencyCtx)];
    case "urgency":
      // Effective urgency LEVEL (deadline-derived for unplaced/auto tasks, like
      // the filter + column); unplaced-without-deadline ("none") sorts least urgent.
      return PRIORITY_RANK[effectiveUrgencyOf(a, urgencyCtx)] - PRIORITY_RANK[effectiveUrgencyOf(b, urgencyCtx)];
    case "title":
      return a.title.localeCompare(b.title);
  }
}
